import {
  LABEEB_PAGE_LIMIT,
  type LabeebConfigInfo,
  type LabeebEnvironmentSettings,
  type LabeebTenantSummary,
  type LabeebTierRow,
} from "../../../api/labeeb";
import { retryOf, statOf } from "../../../components/states";
import { Stat, StatRow } from "../../../components/ui";
import { Subheading } from "../../../f7";
import type { PageOf } from "../../../hooks/usePagedList";
import type { Resource } from "../../../hooks/useResource";

/**
 * Labeeb's headline figures. Every one is a field the docs return.
 *
 * Businesses and tiers are the first page (`limit` 100). A `next_cursor`
 * means there is no total to print, so the tile says "100+" rather than the
 * length of the page we happened to receive. Suspensions are counted from
 * those same rows — there is no count endpoint — and while the cursor is
 * open that count is labelled as the first page.
 */
export function LabeebStrip({
  businesses,
  onBusinessesRetry,
  tiers,
  onTiersRetry,
  configInfo,
  onConfigRetry,
  env,
  onEnvRetry,
}: {
  businesses: Resource<PageOf<LabeebTenantSummary>>;
  onBusinessesRetry: () => void;
  tiers: Resource<PageOf<LabeebTierRow>>;
  onTiersRetry: () => void;
  configInfo: Resource<LabeebConfigInfo>;
  onConfigRetry: () => void;
  env: Resource<LabeebEnvironmentSettings>;
  onEnvRetry: () => void;
}) {
  const businessPartial = businesses.kind === "ready" && businesses.data.next !== null;
  const tierPartial = tiers.kind === "ready" && tiers.data.next !== null;
  const businessFigure = statOf(businesses, (page) => pageCount(page.rows.length, page.next));
  const suspendedFigure = statOf(businesses, (page) =>
    String(page.rows.filter((row) => row.status === "suspended").length),
  );
  const tierFigure = statOf(tiers, (page) => pageCount(page.rows.length, page.next));
  const defaultTier = statOf(configInfo, (info) => info.default_tier ?? null);
  const domains = statOf(env, (doc) => disabledLabel(doc.settings?.disabled_domains));
  const plugins = statOf(env, (doc) => disabledLabel(doc.settings?.disabled_plugins));

  return (
    <section aria-label="Labeeb" className="flex flex-col gap-3">
      <Subheading>Labeeb</Subheading>
      <StatRow>
        <Stat
          label="Businesses"
          to="/tenants"
          value={businessFigure.value}
          hint={businessFigure.hint ?? (businessPartial ? "the list continues" : "registered businesses")}
          onRetry={retryOf(businesses.kind, onBusinessesRetry)}
        />
        <Stat
          label="Suspended"
          to="/tenants"
          value={suspendedFigure.value}
          hint={
            suspendedFigure.hint ??
            (businessPartial ? "on the first page — the list continues" : "from the business list")
          }
          onRetry={retryOf(businesses.kind, onBusinessesRetry)}
        />
        <Stat
          label="Tiers"
          to="/policy/tiers"
          value={tierFigure.value}
          hint={tierFigure.hint ?? (tierPartial ? "the list continues" : "defined")}
          onRetry={retryOf(tiers.kind, onTiersRetry)}
        />
        <Stat
          label="Default tier"
          to="/policy/tiers"
          value={defaultTier.value}
          hint={defaultTier.hint}
          onRetry={retryOf(configInfo.kind, onConfigRetry)}
        />
        <Stat
          label="Domains off"
          to="/policy/settings"
          value={domains.value}
          hint={domains.hint ?? "disabled fleet-wide"}
          onRetry={retryOf(env.kind, onEnvRetry)}
        />
        <Stat
          label="Plugins off"
          to="/policy/settings"
          value={plugins.value}
          hint={plugins.hint ?? "disabled fleet-wide"}
          onRetry={retryOf(env.kind, onEnvRetry)}
        />
      </StatRow>
    </section>
  );
}

/** First-page count. We asked for 100 rows; a cursor means that page is not the fleet. */
function pageCount(count: number, next: string | null): string {
  return next ? `${LABEEB_PAGE_LIMIT}+` : String(count);
}

/**
 * Names disabled fleet-wide, or "none" when the list is present and empty.
 *
 * Absent is not empty. `[]` means nothing is held off; a missing field means
 * the response did not say, and printing "none" would be a fact we were not given.
 */
function disabledLabel(list: string[] | undefined): string | null {
  if (!Array.isArray(list)) return null;
  return list.length === 0 ? "none" : list.join(", ");
}
